import { get, create, updatePatch } from "./apiService";

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface FaqCategory {
  _id: string;
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  order?: number;
  subCategories?: FaqSubCategory[];
  children?: FaqSubCategory[];
}

export interface FaqSubCategory {
  _id: string;
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  parent: string;
  order?: number;
}

export interface Faq {
  _id: string;
  question: string;
  answer: string;
  category: string;
  tags?: string[];
  order?: number;
  isPublished?: boolean;
  isComplianceApproved?: boolean;
  complianceApproved?: boolean;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface FaqSearchResult {
  query: string;
  results: Faq[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  faqs?: Faq[];
}

export interface FaqSearchOptions {
  category?: string;
  limit?: number;
  offset?: number;
}

export interface FaqListResponse {
  faqs: Faq[];
  total: number;
  page: number;
  limit: number;
  [key: string]: unknown;
}

export interface SupportTicket {
  _id: string;
  subject: string;
  description: string;
  message?: string;
  category: string;
  priority: "low" | "medium" | "high";
  status: "open" | "in_progress" | "resolved" | "closed";
  attachments?: string[];
  replies?: TicketReply[];
  createdAt: string;
  updatedAt: string;
  [key: string]: unknown;
}

export interface TicketReply {
  _id: string;
  message: string;
  attachments?: string[];
  createdAt: string;
  [key: string]: unknown;
}

export interface CreateTicketPayload {
  subject: string;
  description?: string;
  message?: string;
  category?: string;
  priority?: "low" | "medium" | "high";
  attachments?: string[];
}

export interface ReplyTicketPayload {
  message: string;
  attachments?: string[];
}

export interface TicketListResponse {
  tickets: SupportTicket[];
  total: number;
  page: number;
  limit: number;
  [key: string]: unknown;
}

// ─── FAQ Categories ─────────────────────────────────────────────────────────

// V2 ENDPOINT: GET /api/v2/support/faq/categories
export const getFaqCategories = async (): Promise<FaqCategory[]> => {
  const categories = await get<FaqCategory[]>("/api/v2/support/faq/categories");

  return categories.map((category) => ({
    ...category,
    subCategories: category.subCategories ?? category.children ?? [],
  }));
};

// V2 ENDPOINT: GET /api/v2/support/faq/categories/:categoryId/sub-categories
export const getFaqSubCategories = async (
  categoryId: string
): Promise<FaqSubCategory[]> => {
  return get<FaqSubCategory[]>(
    `/api/v2/support/faq/categories/${categoryId}/sub-categories`
  );
};

// ─── FAQs ───────────────────────────────────────────────────────────────────

// V2 ENDPOINT: GET /api/v2/support/faq
export const getFaq = async (category?: string): Promise<Faq[]> => {
  const params: Record<string, unknown> = {};
  if (category) params.category = category;
  return get<Faq[]>("/api/v2/support/faq", params);
};

// V2 ENDPOINT: GET /api/v2/support/faqs
export const getFaqs = async (
  page = 1,
  limit = 20,
  category?: string,
  subCategory?: string,
  isPublished?: boolean
): Promise<FaqListResponse> => {
  const params: Record<string, unknown> = { page, limit };
  if (category) params.category = category;
  if (subCategory) params.subCategory = subCategory;
  if (isPublished !== undefined) params.isPublished = isPublished;
  return get<FaqListResponse>("/api/v2/support/faqs", params);
};

// V2 ENDPOINT: GET /api/v2/support/faq/search
export const searchFaq = async (
  q: string,
  options: FaqSearchOptions = {}
): Promise<FaqSearchResult> => {
  const { category, limit = 10, offset = 0 } = options;
  const params: Record<string, unknown> = {
    q: q.trim().slice(0, 500),
    limit,
    offset,
  };
  if (category) params.category = category;
  try {
    const response = await get<
      FaqSearchResult & { faqs?: Faq[]; page?: number }
    >("/api/v2/support/faq/search", params);

    const results = response.results ?? response.faqs ?? [];
    const offset =
      typeof response.offset === "number"
        ? response.offset
        : ((response.page ?? 1) - 1) * limit;

    return {
      query: response.query ?? q,
      results,
      total: response.total ?? results.length,
      limit: response.limit ?? limit,
      offset,
      hasMore:
        typeof response.hasMore === "boolean"
          ? response.hasMore
          : offset + results.length < (response.total ?? results.length),
    };
  } catch {
    return {
      query: q,
      results: [],
      total: 0,
      limit: options.limit ?? 10,
      offset: options.offset ?? 0,
      hasMore: false,
    };
  }
};

// ─── Tickets ────────────────────────────────────────────────────────────────

// V2 ENDPOINT: POST /api/v2/support/tickets
export const createTicket = async (
  data: CreateTicketPayload
): Promise<SupportTicket> => {
  const normalizedDescription = (data.description ?? data.message ?? "").trim();

  return create<SupportTicket>("/api/v2/support/tickets", {
    subject: data.subject.trim(),
    description: normalizedDescription,
    ...(data.message ? { message: data.message.trim() } : {}),
    ...(data.category ? { category: data.category } : {}),
    ...(data.priority ? { priority: data.priority } : {}),
    ...(data.attachments ? { attachments: data.attachments } : {}),
  });
};

// V2 ENDPOINT: GET /api/v2/support/tickets
export const getTickets = async (
  page = 1,
  limit = 20,
  status?: string
): Promise<TicketListResponse> => {
  const params: Record<string, unknown> = { page, limit };
  if (status) params.status = status;
  return get<TicketListResponse>("/api/v2/support/tickets", params);
};

// V2 ENDPOINT: GET /api/v2/support/tickets/:id
export const getTicket = async (ticketId: string): Promise<SupportTicket> => {
  return get<SupportTicket>(`/api/v2/support/tickets/${ticketId}`);
};

// V2 ENDPOINT: POST /api/v2/support/tickets/:id/reply
export const replyToTicket = async (
  ticketId: string,
  data: ReplyTicketPayload
): Promise<unknown> => {
  return create(`/api/v2/support/tickets/${ticketId}/reply`, data);
};

// V2 ENDPOINT: PATCH /api/v2/support/tickets/:id/close
export const closeTicket = async (ticketId: string): Promise<unknown> => {
  return updatePatch(`/api/v2/support/tickets/${ticketId}/close`);
};
